import { useEffect, useMemo, useRef, useState } from "react";
import { Eye, Loader2, Search, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { useWatcherOptions } from "@/hooks/useTasks";
import type { TaskWatcher } from "@/api/types";

/**
 * D-142.W1-C — пікер спостерігачів (native Collaborators, read+stream).
 *
 * Chips + typeahead-пошук active-юзерів (server-owned options, П-10). ≤30.
 * П-10: неактивний ІСНУЮЧИЙ watcher = disabled-chip (видно, не знімається тут,
 * не зникає мовчки). editable=false → read-only chips (watcher без edit не
 * керує складом — backend Forbidden є авторитетним гейтом, П-5).
 */
const MAX_WATCHERS = 30;

export function WatcherPicker({
  value,
  onChange,
  editable,
  excludeIds = [],
  busy = false,
}: {
  value: TaskWatcher[];
  onChange: (next: TaskWatcher[]) => void;
  editable: boolean;
  /** Не пропонувати в пошуку (напр. виконавець — він і так бачить задачу). */
  excludeIds?: string[];
  busy?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const optionsQ = useWatcherOptions(debounced, editable && open);

  // Закриття дропдауна по кліку поза компонентом.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pickedIds = useMemo(() => new Set(value.map((w) => w.id)), [value]);
  const hidden = useMemo(
    () => new Set([...excludeIds, ...pickedIds]),
    [excludeIds, pickedIds],
  );
  const options = (optionsQ.data?.options ?? []).filter((o) => !hidden.has(o.id));
  const atCap = value.length >= MAX_WATCHERS;

  const add = (o: { id: string; name: string }) => {
    if (pickedIds.has(o.id) || atCap) return; // ідемпотентно + ≤30
    onChange([...value, { ...o, isActive: true }]);
    setQuery("");
    setOpen(false);
  };

  const remove = (id: string) => {
    onChange(value.filter((w) => w.id !== id));
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="flex flex-wrap items-center gap-1.5">
        {value.length === 0 && !editable && (
          <span className="text-sm text-muted-foreground">—</span>
        )}
        {value.map((w) => (
          <span
            key={w.id}
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium",
              w.isActive
                ? "border-border bg-muted/40"
                : "border-dashed border-muted-foreground/40 text-muted-foreground",
            )}
            title={w.isActive ? undefined : "Неактивний користувач"}
          >
            <Eye className="h-3 w-3 text-muted-foreground" aria-hidden />
            {w.name}
            {!w.isActive && <span className="text-[10px]">(неактивний)</span>}
            {editable && w.isActive && (
              <button
                type="button"
                onClick={() => remove(w.id)}
                disabled={busy}
                aria-label={`Зняти спостерігача ${w.name}`}
                className="ml-0.5 rounded-full p-0.5 text-muted-foreground hover:text-destructive disabled:opacity-50"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        {editable && !atCap && (
          <div className="relative">
            <div className="flex items-center gap-1.5 rounded-md border px-2 py-1">
              <Search className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
              <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                placeholder="Додати спостерігача…"
                disabled={busy}
                className="w-44 bg-transparent text-xs outline-none disabled:opacity-50"
              />
              {busy && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-hidden />}
            </div>
          </div>
        )}
        {editable && atCap && (
          <span className="text-[11px] text-muted-foreground">максимум {MAX_WATCHERS}</span>
        )}
      </div>
      {editable && open && (
        <div className="absolute z-20 mt-1 max-h-56 w-64 overflow-y-auto rounded-md border bg-background py-1 shadow-lg">
          {optionsQ.isPending ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Пошук…
            </p>
          ) : options.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">Нікого не знайдено</p>
          ) : (
            options.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => add(o)}
                className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted/60"
              >
                {o.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
